import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_URL } from '../../../../../config';

interface StreamingMetric {
  id: string;
  timestamp: number;
  provider: string;
  model: string;
  interviewType: string;
  promptId: string;
  userId: string;
  userName: string;
  ttfbMs: number;
  firstTokenMs: number;
  totalDurationMs: number;
  status: string;
  errorCode?: string;
  region?: string;
  isFallback: boolean;
  environment: string;
  tokenCount?: number;
}

interface MetricsSummary {
  totalCalls: number;
  successCalls: number;
  avgTtfbMs: number;
  avgFirstTokenMs: number;
  avgDurationMs: number;
  underOneSecCount: number;
  underOneSecPercent: number;
  byProvider: Record<string, number>;
  byStatus: Record<string, number>;
  byInterviewType: Record<string, number>;
  byModel: Record<string, number>;
}

@Component({
  selector: 'app-streaming-metrics',
  templateUrl: './streaming-metrics.component.html',
  styleUrls: ['./streaming-metrics.component.scss']
})
export class StreamingMetricsComponent implements OnInit {

  Math = Math; // expose to template

  metrics: StreamingMetric[] = [];
  summary: MetricsSummary | null = null;
  loading = false;
  summaryLoading = false;

  // Pagination
  page = 0;
  size = 10;
  totalPages = 1;
  totalElements = 0;
  paginationPages: number[] = [];

  // Filters
  searchProvider = '';
  searchStatus = '';
  searchInterviewType = '';

  private baseUrl = `${API_URL}/streaming-metrics`;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.fetchMetrics();
    this.fetchSummary();
  }

  fetchMetrics(): void {
    this.loading = true;
    const url = this.hasFilters()
      ? `${this.baseUrl}/search`
      : `${this.baseUrl}/all`;

    const params: any = { page: this.page, size: this.size };
    if (this.searchProvider) params.provider = this.searchProvider;
    if (this.searchStatus) params.status = this.searchStatus;
    if (this.searchInterviewType) params.interviewType = this.searchInterviewType;

    this.http.get<any>(url, { params }).subscribe({
      next: (response) => {
        this.metrics = response?.content || [];
        this.totalPages = response?.totalPages || 1;
        this.totalElements = response?.totalElements || 0;
        this.updatePagination();
        this.loading = false;
      },
      error: () => {
        this.metrics = [];
        this.loading = false;
      }
    });
  }

  fetchSummary(): void {
    this.summaryLoading = true;
    this.http.get<MetricsSummary>(`${this.baseUrl}/summary`).subscribe({
      next: (data) => {
        this.summary = data;
        this.summaryLoading = false;
      },
      error: () => {
        this.summary = null;
        this.summaryLoading = false;
      }
    });
  }

  onSearch(): void {
    this.page = 0;
    this.fetchMetrics();
  }

  clearFilters(): void {
    this.searchProvider = '';
    this.searchStatus = '';
    this.searchInterviewType = '';
    this.page = 0;
    this.fetchMetrics();
  }

  goToPage(p: number): void {
    if (p === -1 || p === this.page) return;
    this.page = p;
    this.fetchMetrics();
  }

  onPageSizeChange(): void {
    this.page = 0;
    this.fetchMetrics();
  }

  formatTimestamp(ts: number): string {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true
    });
  }

  formatMs(ms: number): string {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  isUnderOneSecond(ms: number): boolean {
    return ms > 0 && ms < 1000;
  }

  get sortedMetrics(): StreamingMetric[] {
    return [...this.metrics].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }

  get providerKeys(): string[] {
    return this.summary?.byProvider ? Object.keys(this.summary.byProvider) : [];
  }

  get modelKeys(): string[] {
    return this.summary?.byModel ? Object.keys(this.summary.byModel) : [];
  }

  private hasFilters(): boolean {
    return !!(this.searchProvider || this.searchStatus || this.searchInterviewType);
  }

  private updatePagination(): void {
    const pages: number[] = [];
    const maxVisible = 7;

    if (this.totalPages <= maxVisible) {
      for (let i = 0; i < this.totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      if (this.page > 2) pages.push(-1); // ellipsis
      const start = Math.max(1, this.page - 1);
      const end = Math.min(this.totalPages - 2, this.page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (this.page < this.totalPages - 3) pages.push(-1); // ellipsis
      pages.push(this.totalPages - 1);
    }
    this.paginationPages = pages;
  }

  displayPage(p: number): string {
    return p === -1 ? '...' : String(p + 1);
  }
}
